import useBugsnag from 'hooks/useBugsnag';

const client = require('./request/client');

const { notifyBugsnag } = useBugsnag();

const ENDPOINT = `${process.env.EP_URL}/client_api/app_config.php`;

const VERIFICATION_ON_SIGNUP = 'VERIFICATION_ON_SIGNUP';
const ALLOW_SKIP_SIGNUP_VERIFICATION = 'ALLOW_SKIP_SIGNUP_VERIFICATION';

/** Config values are ENABLED/DISABLED strings, not booleans. */
const isEnabled = (value) => ['enabled', 'true'].includes(String(value).trim().toLowerCase());

/**
 * Pull the name -> value pairs out of a `get_multiple_app_config` response:
 *
 *   { "status": 0,
 *     "message": "Request successful.",
 *     "output": [ { "name": "VERIFICATION_ON_SIGNUP", "status": "ENABLED" } ] }
 *
 * Each entry carries its value under `status` — unrelated to the numeric
 * `status` on the envelope, which reports whether the request itself succeeded.
 */
const readConfigValues = (data) => {
  if (!Array.isArray(data.output)) return null;

  return data.output.reduce((result, entry) => {
    if (entry?.name) result[entry.name] = entry.status;
    return result;
  }, {});
};

/**
 * Fetch one or more app config values.
 *
 * @param {string[]} names
 * @returns {Promise<object|null>} name -> value, or null when the request failed
 *          or the response could not be read.
 */
const getMultiple = async (names) => {
  const data = await client.sendRequest(ENDPOINT, {
    request: 'get_multiple_app_config',
    app_config_names: names.join(','),
  });

  if (!data || data instanceof Error || typeof data.status !== 'number') {
    const error = data instanceof Error ? data : new Error('Unable to reach app config');
    notifyBugsnag({ error, metaData: { scope: 'API REQUEST', xhr_data: { request: { url: ENDPOINT, names } } } });
    return null;
  }

  if (data.status !== 0) return null;

  const values = readConfigValues(data);

  if (!values) {
    // The call succeeded but the payload matched none of the known shapes.
    // Surface it: callers fall back to their defaults, so a change in the
    // response shape would otherwise go unnoticed.
    notifyBugsnag({
      error: new Error('Unrecognised get_multiple_app_config response shape'),
      metaData: {
        scope: 'API REQUEST',
        xhr_data: { request: { url: ENDPOINT, names }, responseKeys: Object.keys(data) },
      },
    });
    return null;
  }

  return values;
};

/**
 * The app config that shapes the ID verification step of signup.
 *
 * - `verificationOnSignup`: whether signup must route through ID verification
 *   between `create_temp_user` and `create_user`.
 * - `allowSkipSignupVerification`: whether that step may be skipped — a "Skip
 *   verification" button, and a way past the step when the ID verification
 *   endpoints are not deployed (404) on this environment.
 *
 * Both default to false when the config cannot be read, so a config outage
 * leaves signup working rather than gating account creation behind a step the
 * site cannot confirm is wanted.
 */
const signupVerification = async () => {
  const values = await getMultiple([VERIFICATION_ON_SIGNUP, ALLOW_SKIP_SIGNUP_VERIFICATION]);

  return {
    verificationOnSignup: isEnabled(values?.[VERIFICATION_ON_SIGNUP]),
    allowSkipSignupVerification: isEnabled(values?.[ALLOW_SKIP_SIGNUP_VERIFICATION]),
  };
};

/** Whether signup must route through ID verification. See signupVerification. */
const verificationOnSignup = async () => (await signupVerification()).verificationOnSignup;

export default {
  getMultiple,
  signupVerification,
  verificationOnSignup,
};
